import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import { LiveChatItem } from 'app/hooks/fetch/useFetchLiveChat';
import { useCallback } from 'react';
import useLinks from 'app/modules/tailor/hooks/useLinks';

import { LinkType } from '../../../hooks/useLinkClickCapture';

function getFormType(data: LiveChatItem) {
  const res = data.items
    ?.map((it) => {
      if (it.params) {
        return it.params.reporting_tags;
      }
    })
    ?.reduce((acc, curr) => {
      if (curr) {
        return acc?.concat(curr);
      }
    });

  return res?.length === 1 ? res[0] : res;
}

export default function useLiveChatTracking() {
  const { shouldShowInterstitial } = useLinks();
  const { captureEventWithActiveScreen } = useAnalytics();

  const captureAgentMessageSent = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatAgentMessageSent);
  }, [captureEventWithActiveScreen]);

  const captureBackButtonClicked = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.BackButtonClicked);
  }, [captureEventWithActiveScreen]);

  const captureCallButtonClicked = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.CallButtonClicked);
  }, [captureEventWithActiveScreen]);

  const captureChatScreen = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatChatScreen);
  }, [captureEventWithActiveScreen]);

  const captureChatSessionNurseJoined = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatSessionNurseJoined);
  }, [captureEventWithActiveScreen]);

  const captureContactFormRequested = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatContactRequested);
  }, [captureEventWithActiveScreen]);

  const captureContactSkipped = useCallback(
    (data: LiveChatItem) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatContactSkipped, {
        contactType: getFormType(data),
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureContactSubmitted = useCallback(
    (data: LiveChatItem) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatContactSubmitted, {
        contactType: getFormType(data),
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureDisclaimerExpanded = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatDisclaimerExpanded);
  }, [captureEventWithActiveScreen]);

  const captureDisclaimerPresented = useCallback(
    (isOnline: boolean) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatDisclaimerPresented, {
        NNOnline: isOnline,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureLinkClicked = useCallback(
    (linkType: LinkType, url: string) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatLinkClicked, {
        linkType,
        url,
        toInterstitial: shouldShowInterstitial(url),
      });
    },
    [captureEventWithActiveScreen, shouldShowInterstitial]
  );

  const captureMinimized = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.Minimized);
  }, [captureEventWithActiveScreen]);

  const captureStartScreen = useCallback(
    (autoOpenDelay: number) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatStartScreen, { autoopenDelay: autoOpenDelay });
    },
    [captureEventWithActiveScreen]
  );

  const captureTermsAccepted = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatTermsAccepted);
  }, [captureEventWithActiveScreen]);

  const captureTermsChecked = useCallback(
    (checked: boolean) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatTermsChecked, {
        value: checked,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureUserMessageSent = useCallback(
    (messagesCount: number) => {
      captureEventWithActiveScreen(PosthogEventName.LiveChatUserMessageSent, {
        isFirst: messagesCount == 1,
        isSecond: messagesCount == 2,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureHalfOpen = useCallback(() => {
    captureEventWithActiveScreen(PosthogEventName.LiveChatHalfOpen);
  }, [captureEventWithActiveScreen]);

  return {
    captureAgentMessageSent,
    captureBackButtonClicked,
    captureCallButtonClicked,
    captureChatScreen,
    captureChatSessionNurseJoined,
    captureContactFormRequested,
    captureContactSkipped,
    captureContactSubmitted,
    captureDisclaimerExpanded,
    captureDisclaimerPresented,
    captureHalfOpen,
    captureLinkClicked,
    captureMinimized,
    captureStartScreen,
    captureTermsAccepted,
    captureTermsChecked,
    captureUserMessageSent,
  };
}
